import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import type { Me } from '@/api/session';
import { useRecordScope } from '@/api/RecordContext';
import { isCompanyRecordPath } from './company-navigation';
import { Button } from '@/ui/primitives';
import { useDepartmentNames } from './departments';
import { PageHeader } from './frame';
import { CONTROL_ROOM_NAME, departmentsForPage, homeOf, ownersOf, routeAllowed, saysItsOwnRefusal, type Scope } from './nav';
import { useRails } from './NavRail';
import { useScope } from './scope';

/**
 * Default-deny at the address bar (ADR-0140; the prototype's
 * `typesForScope`): each scope opens its own pages and nothing else, so a
 * page never renders one scope's rows under another's name. A page opened
 * in the wrong scope opens automatically when its destination is clear;
 * only a genuinely ambiguous destination asks for a choice. The server still decides every read;
 * this only stops the rail lying about where a person is.
 */
export function RouteGuard({ me, children }: { me: Me; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const directory = useDepartmentNames();
  const { scope, options, pick, switching, failed } = useScope(me);
  const rails = useRails(me, directory);
  const path = location.pathname;
  const sourceScope = useRecordScope();
  // CompanyRouteBoundary retains source identifiers/membership; every panel
  // operation is still rechecked by companyRecordCall. Do not turn a source
  // panel into a switch of the whole person's department.
  const sourcePanel = sourceScope !== null && isCompanyRecordPath(path);
  // The default home opens on Workspace in the Control room; an explicit
  // shared-page link (including Today) selects the existing session department.
  if (scope.kind === 'control' && path === '/') return <Navigate to={homeOf(scope)} replace />;
  if (sourcePanel || routeAllowed(path, me, scope, rails(scope)) || saysItsOwnRefusal(path, me)) return switching ? <p role="status" className="p-6 text-secondary">Opening the page…</p> : <>{children}</>;

  const name = (s: Scope) => (s.kind === 'control' ? CONTROL_ROOM_NAME : directory.label(s.name));
  const here = name(scope);
  const owners = ownersOf(path, me, rails, options.filter((s) => s.kind !== scope.kind || (s.kind === 'department' && scope.kind === 'department' && s.name !== scope.name)));
  const declared = departmentsForPage(path);
  const unavailable = owners.length === 0 && declared !== null;
  const destination = declared?.map((department) => directory.label(department)).join(' or ');
  const target = `${path}${location.search}${location.hash}`;
  // A shared page retains the existing session department. Otherwise select
  // only a unique owner; multiple genuine owners still require a choice.
  const automatic = owners.length === 1 ? owners[0] : owners.find((s) => s.kind === 'department' && s.name === me.department);
  if (automatic) return <AutomaticScope key={`${location.key}:${automatic.kind === 'control' ? 'control' : automatic.name}`} scope={automatic} pick={pick} failed={failed} />;
  // The address, record ids and all, is the superadmin's to read (ADR-0140); anyone else is told of "this page".
  const page = me.isSuperadmin ? <span className="font-mono text-dense">{path}</span> : 'This page';
  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-4 p-6 phone:p-4" data-testid="not-in-scope">
      <PageHeader kicker={here} title={owners[0] ? `This is ${name(owners[0])}’s page` : unavailable ? 'Department access is needed' : `Not in ${here}`} />
      <p className="text-secondary">
        {owners[0] ? (
          <>
            {page} belongs to {name(owners[0])}, and every view shows only its own work, so it is
            not opened here under {here}’s name.
          </>
        ) : unavailable ? (
          <>{page} belongs to {destination}. Your current department access does not include it. A superadmin can review that access.</>
        ) : (
          <>{page} is not one of {here}’s pages, nor one of any view you work in.</>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        {owners.map((s) => (
          <Button key={s.kind === 'control' ? 'control' : s.name} variant={s === owners[0] ? 'primary' : 'secondary'} disabled={switching} onClick={() => pick(s, () => void navigate(target, { replace: true, state: location.state }))}>
            Open in {name(s)}
          </Button>
        ))}
        {unavailable && me.isSuperadmin && <Link to="/people" className="inline-flex min-h-tap items-center px-2 underline">Review department access</Link>}
        <Link to={homeOf(scope)} className="inline-flex min-h-tap items-center px-2 underline">
          Back to {scope.kind === 'control' ? 'the Workspace' : 'Today'}
        </Link>
      </div>
    </div>
  );
}

/** One attempt per destination. Refusal is retried only by the person. The
 * existing location is left intact, including its query, hash and state. */
function AutomaticScope({ scope, pick, failed }: { scope: Scope; pick: (scope: Scope) => void; failed: boolean }) {
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    pick(scope);
  }, [scope, pick]);
  if (failed) return (
    <div className="p-6" role="alert">
      <p>The department switch failed. This page has not been opened.</p>
      <Button onClick={() => pick(scope)}>Try again</Button>
    </div>
  );
  return <p role="status" className="p-6 text-secondary">Opening the page…</p>;
}
